import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { Loader2, ArrowRight, ArrowLeft } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) navigate("/dashboard", { replace: true });
    else setError(res.error);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#111111] flex-col justify-between p-12 relative overflow-hidden">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Neo Real Estate Management SaaS" className="h-[77px] w-auto object-contain bg-white rounded-lg p-2 shadow-sm" />
          <div>
            <div className="font-display font-semibold text-white text-2xl tracking-wide">NEO REAL ESTATE</div>
            <div className="text-[22px] text-gray-500 tracking-wide">MANAGEMENT SAAS</div>
          </div>
        </div>
        <div>
          <h1 className="font-display text-5xl font-light text-white leading-tight mb-4">
            {t("不動産管理を、")}<br /><span className="text-brand">{t("ひとつのシステムで。")}</span>
          </h1>
          <p className="text-gray-400 text-sm max-w-md leading-relaxed">
            {t("物件・入居者・契約・家賃・修繕・収支・投資分析まで。")}<br />
            {t("すべての業務を一元管理する次世代の不動産管理プラットフォーム。")}
          </p>
        </div>
        <div className="text-xs text-gray-600">© 2026 Neo Real Estate Management</div>
        <div className="absolute -right-24 -bottom-24 w-96 h-96 border border-white/5 rounded-full" />
        <div className="absolute -right-12 -bottom-12 w-64 h-64 border border-brand/20 rounded-full" />
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-white">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-brand mb-6 transition-colors" data-testid="login-back-home">
            <ArrowLeft className="w-4 h-4" />{t("フロントページに戻る")}
          </Link>
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/logo.png" alt="Neo Real Estate Management SaaS" className="h-[77px] w-auto object-contain" />
            <div>
              <div className="font-display font-semibold text-2xl tracking-wide">NEO REAL ESTATE</div>
              <div className="text-[22px] text-gray-500 tracking-wide">MANAGEMENT SAAS</div>
            </div>
          </div>

          <h2 className="text-2xl font-medium mb-1">{t("ログイン")}</h2>
          <p className="text-sm text-muted-foreground mb-8">{t("アカウント情報を入力してください")}</p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">{t("メールアドレス")}</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required data-testid="login-email" className="h-11" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">{t("パスワード")}</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required data-testid="login-password" className="h-11" />
            </div>
            {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-sm px-3 py-2" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={loading} className="w-full h-11 bg-brand hover:bg-brand-hover text-white" data-testid="login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t("ログイン")}<ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6 text-center" data-testid="login-to-signup">
            アカウントをお持ちでない方は{" "}
            <Link to="/signup" className="text-brand font-medium hover:underline">14日間無料ではじめる</Link>
          </p>

          <div className="mt-8 p-4 bg-gray-50 border border-border rounded-sm text-xs space-y-1.5">
            <div className="font-medium text-gray-700 mb-1">{t("デモアカウント")}</div>
            {[
              { label: t("システム管理者"), email: "ryuichi.kurozaki@gmail.com", password: "Neo@Estate2026" },
              { label: t("不動産管理担当"), email: "manager@neo-estate.co.jp", password: "Manager@2026" },
              { label: t("経理担当"), email: "keiri@neo-estate.co.jp", password: "Keiri@2026" },
              { label: t("閲覧ユーザー"), email: "viewer@neo-estate.co.jp", password: "Viewer@2026" },
            ].map((a) => (
              <button
                key={a.email}
                type="button"
                onClick={() => { setEmail(a.email); setPassword(a.password); setError(""); }}
                className="w-full text-left flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-sm border border-transparent hover:border-brand/40 hover:bg-white transition-colors group"
                data-testid={`demo-account-${a.email}`}
              >
                <span className="text-muted-foreground truncate">
                  <span className="text-gray-700 font-medium">{a.label}</span>：{a.email}
                </span>
                <span className="text-brand opacity-0 group-hover:opacity-100 transition-opacity shrink-0">{t("自動入力")}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
